import { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditorInit } from '../hooks/useEditorInit';
import { useEditorStore } from '../store/editorStore';
import { useImportToProject, useImportingStore } from '../hooks/useImportToProject';
import { useMediaLibraryStore } from '../store/mediaLibraryStore';
import { SafeZoneOverlay } from './SafeZoneOverlay';
import { Loader, Plus } from 'lucide-react';

export function PreviewPanel() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEditorInit(canvasRef);

  const projectAspect = useEditorStore((s) => s.project.projectAspect);
  const { t } = useTranslation();
  const { importAsset } = useImportToProject();
  const importing = useImportingStore((s) => s.importing);
  const hasTracks = useEditorStore((s) => s.project.tracks.length > 0);
  const uploadFile = useMediaLibraryStore((s) => s.uploadFile);
  const [dragOver, setDragOver] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const handleUploadAndImport = useCallback(async (files: FileList | null) => {
    if (!files?.length) return;
    for (const file of Array.from(files)) {
      // Upload to library
      await uploadFile(file);
      // Find the newly added asset (most recent)
      const assets = useMediaLibraryStore.getState().assets;
      const newest = assets.find((a) => a.filename === file.name && a.ingestStatus === 'ready');
      if (newest) {
        await importAsset(newest.id);
      }
    }
  }, [uploadFile, importAsset]);

  // Compute the project frame size that fits inside the container
  const [frame, setFrame] = useState({ w: 0, h: 0 });

  const computeFrame = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    if (cw <= 0 || ch <= 0) return;

    const containerAspect = cw / ch;
    let fw: number, fh: number;

    if (containerAspect > projectAspect) {
      // Container wider than project → fit to height
      fh = ch;
      fw = ch * projectAspect;
    } else {
      // Container taller than project → fit to width
      fw = cw;
      fh = cw / projectAspect;
    }

    setFrame({ w: fw, h: fh });
  }, [projectAspect]);

  // Recompute on mount, resize, and aspect change
  useEffect(() => {
    computeFrame();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => computeFrame());
    ro.observe(el);
    return () => ro.disconnect();
  }, [computeFrame]);

  return (
    <div
      ref={containerRef}
      className={`relative flex items-center justify-center h-full min-h-[200px] bg-neutral-950 overflow-hidden rounded-[var(--radius-inner)] transition-all ${dragOver ? 'ring-2 ring-[var(--user-page-accent)] ring-inset' : ''}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('application/x-vidnight-asset')) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          setDragOver(true);
        }
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const assetId = e.dataTransfer.getData('application/x-vidnight-asset');
        if (assetId) importAsset(assetId);
      }}
    >
      {/* Three.js canvas — always fills entire preview area */}
      <canvas
        ref={canvasRef}
        className="preview-canvas"
        style={{ display: 'block', width: '100%', height: '100%' }}
      />

      {/* CapCut-style dotted project frame — computed to fit container */}
      {frame.w > 0 && frame.h > 0 && (
        <div
          className="absolute pointer-events-none z-[5]"
          style={{
            width: frame.w,
            height: frame.h,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            border: '1px dashed rgba(255, 255, 255, 0.25)',
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
          }}
        />
      )}

      {/* Safe zone guides (togglable) */}
      <SafeZoneOverlay />

      {/* Empty state — click to upload + import, or drag from library */}
      {!hasTracks && !importing && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
          <button
            onClick={() => uploadInputRef.current?.click()}
            className="w-14 h-14 rounded-2xl bg-[var(--user-page-accent)] flex items-center justify-center mb-4 shadow-lg shadow-[var(--user-page-accent)]/20 hover:brightness-110 active:scale-95 transition-all cursor-pointer"
          >
            <Plus size={28} className="text-[var(--user-page-bg)]" strokeWidth={2.5} />
          </button>
          <p className="text-sm font-medium text-white/80">{t('editor.dropToImport', 'Drop video files or click to import')}</p>
          <p className="text-xs text-white/40 mt-1">{t('editor.orDragFromLibrary', 'Or drag from media library')}</p>
          <input
            ref={uploadInputRef}
            type="file"
            accept="video/*,audio/*"
            multiple
            className="hidden"
            onChange={(e) => {
              handleUploadAndImport(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      )}

      {/* Import loading overlay */}
      {importing && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm gap-3">
          <Loader size={56} className="animate-spin text-[var(--user-page-accent)]" />
          <span className="text-xs text-white/70">Preparing media...</span>
        </div>
      )}
    </div>
  );
}
